import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap } from 'rxjs';
import { PaymentReportModel } from '../components/models/payment.report.model';
import { ExportRequestBody, ExportType, FilterService } from '../services/filter.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export interface ReportsPaginationFilters {
  pageIndex: number;
  pageSize: number;
  orderBy?: string;
  orderByDescending: boolean;
  dateStart?: string;
  dateEnd?: string;
  amountMoreThan?: number;
  amountLessThan?: number;
  email?: string;
  receiverAddress?: string;
  blockchainTransactionId?: string;
  blockchainAssetId?: string;
  direction?: 'None' | 'In' | 'Out';
  shortId?: string;
  ascending?: boolean;
}

type ReportsStoreState = {
  loaded: boolean;
  filters: ReportsPaginationFilters;
  activeFilters: boolean;
  total: number;
};

const DEFAULT_PAGE_SIZE = 10;

const initialState: ReportsStoreState = {
  loaded: false,
  activeFilters: false,
  filters: {
    pageIndex: 0,
    direction: 'In',
    pageSize: DEFAULT_PAGE_SIZE,
    orderByDescending: true,
    ascending: false,
  },
  total: 0,
};

export const ReportsStore = signalStore(
  {
    providedIn: 'root',
  },
  withEntities<PaymentReportModel>(),
  withState(() => ({
    ...initialState,
  })),
  withComputed((store) => ({
    totalPages: computed(() => Math.ceil(store.total() / store.filters().pageSize)),
    calculatePaginationRange: computed(() => {
      const start = store.filters().pageIndex * store.filters().pageSize + 1;
      const end = Math.min((store.filters().pageIndex + 1) * store.filters().pageSize, store.total());
      return `${start} - ${end}`;
    }),
    filters: computed(() => {
      return store.filters();
    }),
    activeFilters: computed(() => {
      return store.activeFilters();
    }),
    ascending: computed(() => !store.filters().orderByDescending),
  })),
  withMethods(
    (
      store,
      filterService = inject(FilterService),
      toastr = inject(ToastrService),
      translate = inject(TranslateService),
    ) => {
      const loadReports = rxMethod<void>(
        pipe(
          switchMap(() =>
            filterService.getAllPaymentReports(store.filters()).pipe(
              tapResponse({
                next: ({ total, data }: { total: number; data: PaymentReportModel[] }) => {
                  patchState(store, setAllEntities(data));
                  patchState(store, { total, loaded: true });
                },
                error: (error: { message: string }) => {
                  console.error('Error loading reports:', error);
                },
              }),
            ),
          ),
        ),
      );

      const previousPage = () => {
        if (store.filters().pageIndex > 0) {
          patchState(store, {
            filters: {
              ...store.filters(),
              pageIndex: store.filters().pageIndex - 1,
            },
          });
          loadReports();
        }
      };

      const nextPage = () => {
        patchState(store, {
          filters: {
            ...store.filters(),
            pageIndex: store.filters().pageIndex + 1,
          },
        });
        loadReports();
      };

      const changeItemsPerPage = (pageSize: number) => {
        patchState(store, {
          filters: {
            ...store.filters(),
            pageSize,
          },
        });
        loadReports();
      };

      const exportData = rxMethod<{ type: ExportType; customBody?: Partial<ExportRequestBody> }>(
        pipe(
          switchMap(({ type, customBody }) =>
            filterService.exportData(type, customBody).pipe(
              tapResponse({
                next: (response) => {
                  if (response.status === 200) {
                    toastr.success(translate.instant('exportSuccess'));
                  } else {
                    toastr.error(translate.instant('exportFailed'));
                  }
                },
                error: () => {
                  toastr.error(translate.instant('exportFailed'));
                },
              }),
            ),
          ),
        ),
      );

      const setFilters = (newValue: Partial<ReportsPaginationFilters>) => {
        patchState(store, {
          activeFilters: !!newValue,
          filters: {
            ...store.filters(),
            ...newValue,
          },
        });
        loadReports();
      };

      const toggleSort = () => {
        patchState(store, {
          filters: {
            ...store.filters(),
            pageIndex: 0,
            orderBy: 'createdDate',
            orderByDescending: !store.filters().orderByDescending,
          },
        });
        loadReports();
      };

      const clearFilters = () => {
        patchState(store, {
          activeFilters: false,
          filters: {
            ...store.filters(),
            email: undefined,
            blockchainTransactionId: undefined,
          },
        });
        loadReports();
      };

      const checkIfEmpty = () => {
        if (store.entities().length === 0) {
          return true;
        }
        return false;
      };

      return {
        loadReports,
        previousPage,
        nextPage,
        changeItemsPerPage,
        exportData,
        setFilters,
        toggleSort,
        clearFilters,
        checkIfEmpty,
      };
    },
  ),
);
